import { directSupabase } from '../../lib/supabaseClient';
import { workspaceLifecycleStatus } from '../../utils/workflowUtils';
import {
  assembleWorkspaceOperations,
  emptyWorkspaceOperations,
  type OpsAssignee,
  type OpsDocument,
  type OpsJobCard,
  type OpsProject,
  type OpsProjectEvent,
  type OpsShareLink,
  type WorkspaceOperations,
  type WorkspaceSnapshot,
} from './workspaceDashboard.model';
import { normalizeInvoiceRole } from '../../modules/docs/utils/invoiceLedger';
import { stageFor } from './workspaceStages';
import { loadActualsDueProjectIds } from '../../services/jobReviews.service';
import { loadJobCardCompletions } from './openJobCards.service';
import type { JobCompletionRow } from './openJobCards.model';

const PROJECT_COLUMNS = [
  'id',
  'project_name',
  'customer_name',
  'supply_only',
  'complete',
  'project_status',
  'status',
  'workflow_tasks',
].join(', ');

const DOCUMENT_COLUMNS = [
  'id',
  'project_id',
  'kind',
  'status',
  'gross_total',
  'issued_at',
  'paid_at',
  'sent_at',
  'accepted_at',
  'created_at',
  'invoice_role',
  'payment_terms_in_days',
  'deposit_amount',
  'has_deposit',
  'invoice_ref',
].join(', ');

async function inChunks<T>(ids: string[], load: (chunk: string[]) => Promise<T[]>): Promise<T[]> {
  const unique = [...new Set(ids.filter(Boolean))];
  const rows: T[] = [];
  for (let index = 0; index < unique.length; index += 200) {
    rows.push(...await load(unique.slice(index, index + 200)));
  }
  return rows;
}

function missingColumn(message?: string, column?: string) {
  return !!message && !!column && new RegExp(column, 'i').test(message);
}

async function loadOpsProjects(projectIds?: string[]): Promise<OpsProject[]> {
  if (projectIds) {
    return inChunks<OpsProject>(projectIds, async (chunk) => {
      const { data, error } = await directSupabase.from('projects').select(PROJECT_COLUMNS).in('id', chunk);
      if (error) throw new Error(error.message);
      return (data || []) as unknown as OpsProject[];
    });
  }
  const { data: projectRows, error: projectError } = await directSupabase
    .from('projects')
    .select(PROJECT_COLUMNS)
    .limit(5000);
  if (projectError) throw new Error(projectError.message);
  return (projectRows || []) as unknown as OpsProject[];
}

/**
 * Board / Today data. With `projectIds`, only those workspaces are loaded, so other pages
 * can ask "where is this job?" and get exactly the Board's answer.
 */
export async function loadWorkspaceOperations(
  now = new Date(),
  options: { projectIds?: string[] } = {},
): Promise<WorkspaceOperations> {
  const projects = await loadOpsProjects(options.projectIds);
  const activeIds = projects
    .filter((project) => workspaceLifecycleStatus(project) === 'Active')
    .map((project) => project.id);

  if (!activeIds.length) return emptyWorkspaceOperations();

  const documents = await inChunks<OpsDocument>(activeIds, async (chunk) => {
    const first = await (directSupabase as any)
      .from('documents')
      .select(DOCUMENT_COLUMNS)
      .in('project_id', chunk)
      .neq('status', 'void');
    if (!first.error) return (first.data || []) as OpsDocument[];
    if (!missingColumn(first.error.message, 'payment_terms_in_days')
      && !missingColumn(first.error.message, 'invoice_ref')
      && !missingColumn(first.error.message, 'issued_at')
      && !missingColumn(first.error.message, 'invoice_role')) {
      throw new Error(first.error.message);
    }
    const fallback = await (directSupabase as any)
      .from('documents')
      .select('id, project_id, kind, status, gross_total, paid_at, sent_at, accepted_at, created_at, deposit_amount, has_deposit')
      .in('project_id', chunk)
      .neq('status', 'void');
    if (fallback.error) throw new Error(fallback.error.message);
    return (fallback.data || []) as OpsDocument[];
  });

  const cards = await inChunks<OpsJobCard>(activeIds, async (chunk) => {
    let overrideColumns = ['install_date_override', 'install_end_date_override', 'follows_booking'];
    for (;;) {
      const result = await (directSupabase as any)
        .from('project_job_cards')
        .select(['id', 'project_id', 'title', ...overrideColumns].join(', '))
        .in('project_id', chunk);
      if (!result.error) return (result.data || []) as OpsJobCard[];
      const missing = overrideColumns.find((column) => missingColumn(result.error.message, `\\b${column}\\b`));
      if (!missing) throw new Error(result.error.message);
      overrideColumns = overrideColumns.filter((column) => column !== missing);
    }
  });

  const assigneesRaw = await inChunks<{ job_card_id: string; project_id: string; profile_id: string }>(activeIds, async (chunk) => {
    const { data, error } = await (directSupabase as any)
      .from('project_job_assignees')
      .select('job_card_id, project_id, profile_id')
      .in('project_id', chunk);
    if (error) throw new Error(error.message);
    return data || [];
  });
  const profileIds = [...new Set(assigneesRaw.map((row) => row.profile_id).filter(Boolean))];
  const names = new Map<string, string>();
  if (profileIds.length) {
    const { data, error } = await directSupabase
      .from('profiles')
      .select('id, full_name')
      .in('id', profileIds);
    if (!error) {
      (data || []).forEach((row: { id: string; full_name?: string | null }) => {
        if (row.full_name) names.set(row.id, row.full_name);
      });
    }
  }
  const assignees: OpsAssignee[] = assigneesRaw.map((row) => ({
    job_card_id: row.job_card_id,
    project_id: row.project_id,
    full_name: names.get(row.profile_id) || null,
  }));

  const events = await inChunks<OpsProjectEvent>(activeIds, async (chunk) => {
    const { data, error } = await directSupabase
      .from('project_events')
      .select('project_id, event_type, event_date, end_date, metadata, status, email_status, email_sent_at, created_at')
      .in('project_id', chunk)
      .in('event_type', [
        'installationDateConfirmation',
        'deliveryDateConfirmation',
        'depositRequest',
        'firstSurveyAppointmentConfirmation',
        'finalSurveyAppointmentConfirmation',
        'requestReview',
      ]);
    if (error) throw new Error(error.message);
    return (data || []) as unknown as OpsProjectEvent[];
  });

  const finalSurveys = await inChunks<{ project_id: string; completed_at: string | null }>(
    activeIds,
    async (chunk) => {
      const { data, error } = await (directSupabase as any)
        .from('visual_designer_surveys')
        .select('project_id, completed_at')
        .in('project_id', chunk)
        .eq('kind', 'final')
        .eq('completion_status', 'complete');
      if (error) throw new Error(error.message);
      return data || [];
    },
  );

  const depositInvoiceIds = documents
    .filter((doc) => doc.kind === 'Invoice' && normalizeInvoiceRole(doc.invoice_role) === 'deposit')
    .map((doc) => doc.id);
  const shareLinks = await inChunks<OpsShareLink>(depositInvoiceIds, async (chunk) => {
    const { data, error } = await (directSupabase as any)
      .from('document_share_links')
      .select('document_id, created_at, last_sent_at')
      .in('document_id', chunk);
    if (error) throw new Error(error.message);
    return (data || []) as OpsShareLink[];
  });

  const completions = await inChunks<JobCompletionRow>(
    Array.from(new Set(cards.map((card) => card.project_id))),
    loadJobCardCompletions,
  );

  const activeSet = new Set(activeIds);
  const actualsDueProjectIds = await loadActualsDueProjectIds(
    projects.filter((project) => activeSet.has(project.id)),
    events.filter((event) => event.event_type === 'installationDateConfirmation' || event.event_type === 'deliveryDateConfirmation'),
    { cards, completions },
  );

  return assembleWorkspaceOperations({
    projects,
    documents,
    cards,
    assignees,
    events,
    shareLinks,
    completions,
    finalSurveys,
    actualsDueProjectIds,
    now,
  });
}

/**
 * Where each workspace is, keyed by project id. Active workspaces get the Board's snapshot;
 * completed and cancelled ones are answered from their status without loading anything.
 */
export async function loadWorkspaceSnapshots(
  projects: Array<OpsProject & { completed_at?: string | null; cancelled_at?: string | null }>,
  now = new Date(),
): Promise<Record<string, WorkspaceSnapshot>> {
  const out: Record<string, WorkspaceSnapshot> = {};
  const activeIds: string[] = [];
  projects.forEach((project) => {
    const lifecycle = workspaceLifecycleStatus(project);
    if (lifecycle === 'Active') {
      activeIds.push(project.id);
      return;
    }
    out[project.id] = {
      projectId: project.id,
      stage: stageFor(lifecycle),
      state: null,
      stateLabel: lifecycle,
      exception: false,
      flags: [],
      reason: null,
      side: null,
      next: null,
      href: `/projects/${encodeURIComponent(project.id)}/workspace`,
      recordTicks: {},
    };
  });
  if (activeIds.length) {
    const operations = await loadWorkspaceOperations(now, { projectIds: activeIds });
    Object.assign(out, operations.snapshots);
  }
  return out;
}
